import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../../orpc'

// Notification settings types ("Notify me about" toggles)
type NotificationSettings = {
  notifyRoster: boolean
  notifyFlights: boolean
  notifyTrips: boolean
  notifyAnnouncements: boolean
  notifyChat: boolean
  notifyBirthdays: boolean
  notifyDocumentExpiry: boolean
  notifyMaintenance: boolean
  notifyPaxUpdates: boolean
}

// Regional settings types
type RegionalSettings = {
  temperatureUnit: 'Fahrenheit' | 'Celsius'
  timezoneDisplay: 'UTC' | 'Local' | 'Zulu'
}

export function useCrewGetNotificationSettings() {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.crew.getNotificationSettings.queryOptions())
}

export function useCrewUpdateNotificationSettings() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()
  const { queryKey } = orpc.crew.getNotificationSettings.queryOptions()

  return useMutation(
    orpc.crew.updateNotificationSettings.mutationOptions({
      onMutate: async (newSettings) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({ queryKey })

        // Snapshot the previous value
        const previousSettings =
          queryClient.getQueryData<NotificationSettings>(queryKey)

        // Optimistically update to the new value
        queryClient.setQueryData<NotificationSettings>(queryKey, {
          ...previousSettings,
          ...newSettings,
        } as NotificationSettings)

        // Return context with the previous value for rollback
        return { previousSettings }
      },
      onError: (_err, _newSettings, context) => {
        // Rollback to the previous value on error
        if (context?.previousSettings) {
          queryClient.setQueryData(queryKey, context.previousSettings)
        }
      },
      onSettled: () => {
        // Refetch after error or success to ensure cache is in sync
        queryClient.invalidateQueries({ queryKey })
      },
    })
  )
}

export function useCrewGetRegionalSettings() {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.crew.getRegionalSettings.queryOptions())
}

export function useCrewUpdateRegionalSettings() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()
  const { queryKey } = orpc.crew.getRegionalSettings.queryOptions()

  return useMutation(
    orpc.crew.updateRegionalSettings.mutationOptions({
      onMutate: async (newSettings) => {
        await queryClient.cancelQueries({ queryKey })

        const previousSettings =
          queryClient.getQueryData<RegionalSettings>(queryKey)

        queryClient.setQueryData<RegionalSettings>(queryKey, {
          ...previousSettings,
          ...newSettings,
        } as RegionalSettings)

        return { previousSettings }
      },
      onError: (_err, _newSettings, context) => {
        if (context?.previousSettings) {
          queryClient.setQueryData(queryKey, context.previousSettings)
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey })
        // Also invalidate crew member queries so components reading temperatureUnit from the crew profile get updated
        queryClient.invalidateQueries({ queryKey: orpc.crew.key() })
      },
    })
  )
}
